import { FC, useEffect, useState } from 'react';
import {
  BoxV2 as Box,
  ButtonV2 as Button,
  DropdownV2 as Dropdown,
  Flex,
} from 'portal-commons';
import { Controller, useForm } from 'react-hook-form';

import { CampaignDetail } from '@/apis/core';
import { getCnpListOptions } from '@/apis/services';
import { SelectItem } from '@/utils/types';

interface ElectCnpFormValues {
  cnpUid: string;
}

interface ElectCnpFormProps {
  campaign: CampaignDetail;
  onCancel: () => void;
  onSave: (cnpUid: string) => void;
  processing?: boolean;
}

const ElectCnpForm: FC<ElectCnpFormProps> = ({
  campaign,
  onCancel,
  onSave,
  processing,
}) => {
  const [options, setOptions] = useState<SelectItem[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm<ElectCnpFormValues>({
    mode: 'onChange',
    defaultValues: {
      cnpUid: '',
    },
  });

  useEffect(() => {
    const fetchOptions = async () => {
      setLoadingOptions(true);
      const opts = await getCnpListOptions();
      setOptions(opts || []);
      setLoadingOptions(false);
    };
    fetchOptions();
    return () => {
      setLoadingOptions(false);
    };
  }, [campaign]);

  const onSubmit = (values: ElectCnpFormValues) => {
    onSave(values.cnpUid);
  };

  return (
    <form
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      style={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%',
          flex: 1,
        }}
      >
        <Flex
          sx={{
            flexDirection: 'column',
            gap: '24px',
            height: '100%',
          }}
        >
          <Flex
            sx={{
              flexDirection: 'column',
              gap: '12px',
              width: '100%',
              lineHeight: 'normal',
            }}
          >
            <Box
              as="h2"
              sx={{
                fontSize: '28px',
                fontWeight: 500,
                color: '#19262A',
              }}
            >
              Elect Connectivity Partner
            </Box>
            <Box
              sx={{
                fontSize: '16px',
                color: '#4D5659',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              <p style={{ margin: 0 }}>
                Please identify the partner you will be forwarding the message
                program to. By making a selection you are automatically
                accepting the program that has been shared with you. Failing to
                elect a CNP may hinder the program from reaching the MNO.
              </p>
              <p style={{ margin: 0 }}>
                For Sole Proprietor programs, if your CNP is not showing in the
                list, it means they are not enabled to receive Sole Proprietor
                programs. Please reach out to your CNP for more details.
              </p>
            </Box>
          </Flex>

          <Flex
            sx={{
              flexDirection: 'column',
              gap: '24px',
              width: '100%',
              flex: 1,
            }}
          >
            <Box
              sx={{
                fontSize: '14px',
                color: '#4D5659',
              }}
            >
              * Required fields.
            </Box>

            <Controller
              name="cnpUid"
              control={control}
              rules={{
                required: 'Required',
              }}
              render={({ field: { value, onChange } }) => (
                <Dropdown
                  required
                  error={!!errors.cnpUid}
                  helperText={errors.cnpUid?.message}
                  label="Select a Connectivity Partner"
                  options={options}
                  value={value}
                  onChange={onChange}
                  disabled={processing || loadingOptions}
                  data-testid="electCnpDropdown"
                />
              )}
            />
          </Flex>
        </Flex>
        <Flex
          sx={{
            justifyContent: 'flex-end',
            gap: '16px',
            width: '100%',
            pt: '24px',
            mt: 'auto',
          }}
        >
          <Button
            size="lg"
            shape="square"
            variant="outline"
            color="primary"
            onClick={onCancel}
            disabled={processing}
            type="button"
            data-testid="cancelElectCnp"
          >
            Cancel
          </Button>
          <Button
            size="lg"
            shape="square"
            variant="fill"
            color="primary"
            disabled={!isValid || processing}
            type="submit"
            data-testid="saveElectCnp"
          >
            Proceed
          </Button>
        </Flex>
      </Flex>
    </form>
  );
};

export default ElectCnpForm;
